import type { Platform } from '@sparklayer-io/spark-dash-ui/types/platform';
import { useQuery } from '@tanstack/react-query';
import { t } from 'i18next';
import { createContext, type PropsWithChildren, useContext } from 'react';
import { SparkSuspense } from '../components/spark-suspense';
import type { Environment } from '../constants/platform';
import { useHandlers, useSite } from '../context/page-context';

export type BillingProvider = 'shopify' | 'stripe' | 'hubspot' | 'wix' | 'test';

export type { Environment, Platform };

export type BillingPlanKey =
  | 'enterprise'
  | 'custom'
  // Basic Plan
  | 'basic_free'
  // Starter Plan
  | 'v2_basic'
  // Growth Plan
  | 'v2_growth'
  // Pro Plan
  | 'v2_pro'
  | 'v2_starter_plus'
  | 'development'
  | 'enterprise_onboarding'
  // Deprecated plans
  | 'csv_only'
  | 'professional';

export type BillingDuration = 'month' | 'year';

type TeamBillingPlanState =
  | 'active'
  | 'pending'
  | 'pending_provider_confirmation'
  | 'pending_cancellation'
  | 'archived';

type BillingPlan = {
  key: BillingPlanKey;
  price: number;
  orders_allowed: number;
  sales_agents_allowed: number;
  advanced_features_enabled: boolean;
  public_name: string;
  is_deprecated: boolean;
};

export type Team = {
  id: number;
  name: string;
  is_partner: boolean;
  slugged_name: string;
  website: string;
  app_registration_source: string;
  billing_plan_name?: string;
  shopify_enterprise_billing: boolean;
  shopify_enterprise_planned_go_live?: Date;
  billing_custom_price?: number;
  orders_allowed?: number;
  shopify_enterprise_billing_no_of_teams_allowed?: number;
  billing_linked_team_id?: number;
  billing_linked_site_id?: string;
  shopify_enterprise_billing_completed: boolean;
  shopify_recurring_trial_ends_at?: Date;
  billing_plan_data: BillingPlan;
  advanced_features_enabled: boolean;
  enterprise_features_enabled: boolean;
  billing_provider?: BillingProvider;
  billing_duration?: string;
  sales_agents_allowed?: number;
  is_scheduled_for_deletion: boolean;
};

type TeamBillingPlan = {
  id: number;
  team_id: number;
  state: TeamBillingPlanState;
  billing_provider_name: BillingProvider;
  plan_key: BillingPlanKey;
  duration: BillingDuration;
  trial_ends_at?: Date;
  billing_plan: BillingPlan;
};

export type TeamBillingResource = {
  has_active_plan: boolean;
  trial_ends_at?: Date;
  trial_ends_in_days?: number;
  has_trial_expired: boolean;
  is_enterprise_billing: boolean;
  provider?: BillingProvider;
  plan_name?: string;
  plan_key?: BillingPlanKey;
  price_cents?: number;
  sales_agents_allowed?: number;
  orders_allowed?: number;
  teams_allowed?: number | null;
  is_deprecated?: boolean | null;
  duration?: BillingDuration;
  is_advanced_features_enabled?: boolean;
  has_pending_plan: boolean;
  pending_plan?: TeamBillingPlan;
  allow_change_plan: boolean;
  max_price_lists?: number | null;
};

export type Env = {
  id: number;
  environment: Environment;
  platform?: Platform;
  store_id?: string;
  db_created: boolean;
  onboarding_platform: boolean;
  onboarding_api: boolean;
  onboarding_sync: boolean;
  onboarding_frontend: boolean;
  onboarding_launch: boolean;
  onboarding_plus: boolean;
  onboarding_settings: boolean;
  created_at?: string;
  platform_removed_at?: Date;
  /** Version string (e.g. `3.7.1`) or `latest`; null when the store has never been pinned. */
  sparkjs_version?: string | null;
  team: Team;
  billing: TeamBillingResource;
  connected_platform?: Platform;
};

export { basePlatform, isLive, isShopify, isTest, platformLabel } from '../constants/platform';

export const ENV_QUERY_KEY = 'get-env';

const EnvContext = createContext<Env | undefined>(undefined);

export function useEnv(): Env {
  const context = useContext(EnvContext);
  if (!context) throw new Error('useEnv must be used within an EnvProvider');
  return context;
}

export function EnvProvider({ children }: PropsWithChildren) {
  const { apis } = useHandlers();
  const site = useSite();

  async function fetchEnv() {
    return await apis.site.env(site.siteId, site.siteEnv);
  }

  const {
    data: env,
    isLoading,
    isError,
    refetch,
  } = useQuery<Env>({
    queryKey: [ENV_QUERY_KEY, site.siteId, site.siteEnv],
    queryFn: fetchEnv,
    throwOnError: true,
  });

  return (
    <EnvContext.Provider value={env}>
      <SparkSuspense
        isLoading={isLoading || !env}
        isError={isError}
        onReload={refetch}
        errorMessage={t('errors:generic')}
      >
        {children}
      </SparkSuspense>
    </EnvContext.Provider>
  );
}
